<template>
  <div class="order-distribution-chart">
    <div class="chart-header">
      <h3 class="chart-title">订单数量分布</h3>
      <a-radio-group v-model="orderType" button-style="solid" size="small" @change="handleTypeChange">
        <a-radio-button value="0">代收</a-radio-button>
        <a-radio-button value="1">代付</a-radio-button>
      </a-radio-group>
    </div>

    <div class="chart-container" ref="chartContainer" :style="{ height: '300px' }">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <a-spin size="large" tip="图表加载中..." />
      </div>
      <!-- 无数据状态 -->
      <div v-else-if="!hasData" class="no-data-container">
        <div class="no-data-text">暂无数据</div>
        <!-- <a-empty description="暂无数据" /> -->
      </div>
    </div>
  </div>
</template>

<script>
import { getChartComponent } from '@/utils/chartLoader'

export default {
  name: 'OrderCountChart',
  props: {
    chartData: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      orderType: '0',
      chart: null,
      loading: false,
      Column: null // 动态加载的图表类
    }
  },
  computed: {
    hasData () {
      const data = this.chartData || {}
      const todayData = data.todayCount || []
      const yesterdayData = data.yesterdayCount || []
      return todayData.length > 0 || yesterdayData.length > 0
    }
  },
  watch: {
    chartData: {
      handler (newData) {
        // console.log('chartData changed:', newData)
        if (newData && Object.keys(newData).length > 0) {
          this.$nextTick(() => {
            this.renderChart()
          })
        }
      },
      deep: true,
      immediate: true
    }
  },
  async mounted () {
    // 动态导入G2Plot
    await this.loadG2Plot()
    this.$nextTick(() => {
      this.initChart()
    })
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resizeChart)
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  },
  methods: {
    // 动态加载G2Plot
    async loadG2Plot () {
      try {
        // console.log('OrderCountChart: 开始加载G2Plot Column组件...')
        this.Column = await getChartComponent('Column')
        // console.log('OrderCountChart: G2Plot Column组件加载成功')
      } catch (error) {
        // console.error('OrderCountChart: G2Plot Column组件加载失败:', error)
        this.$message.error('订单数量图表组件加载失败')
        this.showFallbackChart()
      }
    },

    // Fallback图表显示
    showFallbackChart () {
    //   console.log('OrderCountChart: 显示fallback图表')
      const container = this.$refs.chartContainer
      if (container) {
        container.innerHTML = `
          <div style="display: flex; justify-content: center; align-items: center; height: 300px; background: #f5f5f5; border-radius: 6px;">
            <div style="text-align: center;">
              <p style="margin: 0; color: #666; font-size: 14px;">订单数量图表加载中...</p>
              <p style="margin: 8px 0 0 0; color: #999; font-size: 12px;">如果长时间未显示，请刷新页面重试</p>
            </div>
          </div>
        `
      }
    },

    // 初始化图表
    initChart () {
      if (!this.$refs.chartContainer || !this.Column) {
        console.warn('OrderCountChart: Chart container not found or G2Plot not loaded')
        return
      }
      this.renderChart()
    },

    // 渲染图表
    async renderChart () {
      try {
        if (!this.$refs.chartContainer || !this.Column) {
          console.warn('OrderCountChart: Chart container not found or G2Plot not loaded, skip rendering')
          return
        }

        if (this.chart) {
          this.chart.destroy()
          this.chart = null
        }

        const data = this.chartData || {}
        const todayData = data.todayCount || []
        const yesterdayData = data.yesterdayCount || []

        // console.log('OrderCountChart: Rendering chart with data:', { todayData, yesterdayData })

        if (!this.hasData) {
          console.log('OrderCountChart: No data available for chart')
          return
        }

        this.loading = true

        // 过滤和处理数据，API数据是1-24完整范围
        const processData = (rawData) => {
          return rawData
            .filter(item => {
              const hour = parseInt(item.hour)
              return hour >= 1 && hour <= 24 // 保留1-24的完整小时范围
            })
            .map(item => ({
              hour: parseInt(item.hour),
              orderCount: parseInt(item.orderCount) || 0
            }))
        }

        const processedTodayData = processData(todayData)
        const processedYesterdayData = processData(yesterdayData)

        // 构建小时数据映射 (API数据是1-24)
        const todayDataMap = new Map(processedTodayData.map(item => [item.hour, item]))
        const yesterdayDataMap = new Map(processedYesterdayData.map(item => [item.hour, item]))
        console.log('OrderCountChart: todayDataMap:', todayDataMap)
        console.log('OrderCountChart: yesterdayDataMap:', yesterdayDataMap)

        // 生成24小时完整数据，显示1-24
        const hours = Array.from({ length: 24 }, (_, i) => (i + 1).toString()) // hour 字符串
        const chartData = []

        hours.forEach(hour => {
          const todayItem = todayDataMap.get(Number(hour)) || { hour: Number(hour), orderCount: 0 }
          const yesterdayItem = yesterdayDataMap.get(Number(hour)) || { hour: Number(hour), orderCount: 0 }

          chartData.push({
            hour: hour, // 字符串
            type: '今日',
            value: todayItem.orderCount
          })
          chartData.push({
            hour: hour, // 字符串
            type: '昨日',
            value: yesterdayItem.orderCount
          })
        })
        console.log('OrderCountChart: chartData:', chartData)

        // 验证数据有效性
        if (!chartData || chartData.length === 0) {
          console.warn('OrderCountChart: No valid chart data available')
          return
        }

        // 验证数据结构
        const isDataValid = chartData.every(item =>
          typeof item.hour === 'string' &&
          typeof item.type === 'string' &&
          typeof item.value === 'number' &&
          !isNaN(item.value)
        )

        if (!isDataValid) {
          console.error('OrderCountChart: Invalid data structure detected')
          this.$message.error('图表数据格式错误')
          return
        }

        // 确保容器存在且有尺寸
        if (!this.$refs.chartContainer || this.$refs.chartContainer.offsetWidth === 0) {
          console.warn('OrderCountChart: Chart container has no width, retrying...')
          setTimeout(() => this.renderChart(), 100)
          return
        }

        console.log('OrderCountChart: Container width:', this.$refs.chartContainer.offsetWidth)
        console.log('OrderCountChart: Creating chart with Column class:', this.Column)

        try {
          this.chart = new this.Column(this.$refs.chartContainer, {
            data: chartData,
            xField: 'hour',
            yField: 'value',
            seriesField: 'type',
            isGroup: true,
            columnStyle: {
              radius: [2, 2, 0, 0]
            },
            color: ['#1890ff', '#52c41a'],
            legend: {
              position: 'top-right'
            },
            tooltip: {
              shared: true,
              showMarkers: false,
              // title: (datum) => `时间：${parseInt(datum.hour) + 1}`,
              formatter: (datum) => {
                return {
                  name: datum.type,
                  value: `${datum.value} 笔`
                }
              }
            },
            yAxis: {
              title: {
                text: '订单数量 (笔)'
              },
              grid: {
                line: {
                  style: {
                    stroke: '#f0f0f0',
                    lineWidth: 1,
                    lineDash: [4, 4]
                  }
                }
              }
            },
            xAxis: {
              type: 'category',
              title: {
                text: '时间'
              },
              label: {
                autoRotate: false,
                style: {
                  fontSize: 11,
                  fill: '#666'
                }
              }
            },
            label: {
              position: 'top',
              style: {
                fontSize: 10,
                fill: '#666'
              },
              formatter: (text) => {
                return text > 0 ? text : ''
              }
            },
            animation: {
              appear: {
                animation: 'fade-in',
                duration: 600
              }
            }
          })

          console.log('OrderCountChart: About to render chart...')
          this.chart.render()
          this.loading = false
          console.log('OrderCountChart: Chart rendered successfully')
        } catch (renderError) {
          this.loading = false
          console.error('OrderCountChart: Chart render failed:', renderError)

          // 尝试不使用动画重新渲染
          try {
            console.log('OrderCountChart: Retrying without animation...')
            if (this.chart) {
              this.chart.destroy()
              this.chart = null
            }

            this.chart = new this.Column(this.$refs.chartContainer, {
              data: chartData,
              xField: 'hour',
              yField: 'value',
              seriesField: 'type',
              isGroup: true,
              columnStyle: {
                radius: [2, 2, 0, 0]
              },
              color: ['#1890ff', '#52c41a'],
              legend: {
                position: 'top-right'
              },
              tooltip: {
                shared: true,
                showMarkers: false,
                title: (datum) => `时间：${parseInt(datum.hour) + 1}`,
                formatter: (datum) => {
                  return {
                    name: datum.type,
                    value: `${datum.value} 笔`
                  }
                }
              },
              yAxis: {
                title: {
                  text: '订单数量 (笔)'
                },
                grid: {
                  line: {
                    style: {
                      stroke: '#f0f0f0',
                      lineWidth: 1,
                      lineDash: [4, 4]
                    }
                  }
                }
              },
              xAxis: {
                type: 'category',
                title: {
                  text: '时间'
                },
                label: {
                  autoRotate: false,
                  style: {
                    fontSize: 11,
                    fill: '#666'
                  }
                }
              },
              label: {
                position: 'top',
                style: {
                  fontSize: 10,
                  fill: '#666'
                },
                formatter: (text) => {
                  return text > 0 ? text : ''
                }
              },
              animation: false // 禁用动画
            })

            this.chart.render()
            console.log('OrderCountChart: Chart rendered successfully without animation')
          } catch (fallbackError) {
            console.error('OrderCountChart: Fallback render also failed:', fallbackError)
            this.$message.error('订单数量图表渲染失败，请刷新页面重试')
            this.showFallbackChart()
          }
        }
      } catch (error) {
        this.loading = false
        console.error('OrderCountChart: Error rendering chart:', error)
        // 显示错误信息而不是崩溃
        this.$message.error('订单数量图表渲染失败: ' + error.message)
        this.showFallbackChart()
      }
    },

    // 图表自适应
    resizeChart () {
      if (this.chart) {
        try {
          this.chart.forceFit()
        } catch (error) {
          console.error('OrderCountChart: Error resizing chart:', error)
        }
      }
    },

    // 处理类型变化
    handleTypeChange () {
      this.$emit('handleOrderCountTypeChange', this.orderType)
    }
  }
}
</script>

<style lang="less" scoped>
.order-distribution-chart {
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .chart-title {
      margin: 0;
      font-size: 16px;
      font-weight: 500;
      color: #333;
    }
  }

  .chart-container {
    width: 100%;
    position: relative;

    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 300px;
    }

    .no-data-container {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 300px;
    }
  }
}
</style>
